import first from 'lodash/first.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';

import { isCorporateClient } from '@/composables/useEdverumClient.js';
import { handleResponseError } from '@/Helpers.js';
import Remediation from '@/models/Remediation.js';
import axios from '@/plugins/axios.js';
import { gate } from '@/plugins/vue-gate/Index.js';
import store from '@/store/Index.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';
import { isAttemptLockdownRouteName } from '@/utilities/sidebarMenuRoute.js';

/**
 * Routes a learner in a locked evaluation may be on. Identity verification can
 * interrupt any attempt (CheckIdentityVerificationAttempt), so it stays reachable.
 */
const LOCKED_EVALUATION_ROUTE_NAMES = Object.freeze(['automated-survey-attempt', 'identity-verification-attempt']);

/**
 * The locked evaluation route the learner last left. Identity verification can
 * interrupt the evaluation, so a navigation that starts there still needs to know
 * which evaluation to send the learner back to before any await.
 */
let lastLockedEvaluationRoute = null;

const hasValue = value => value !== undefined && value !== null && value !== '';

/**
 * Whether evaluation route params point at the given assignment / attempt. The
 * attempt id is optional on the route, so it only has to match when both sides have one.
 *
 * @param {Object} params Route params of an `automated-survey-attempt` route.
 * @param {{ assignmentId: *, attemptId: * }} evaluation
 * @returns {boolean}
 */
const isSameEvaluation = (params, evaluation) =>
    String(params?.assignmentId) === String(evaluation.assignmentId) &&
    (!hasValue(params?.attemptId) ||
        !hasValue(evaluation.attemptId) ||
        String(params.attemptId) === String(evaluation.attemptId));

/**
 * Whether a learner locked into the given evaluation may navigate to `to`.
 *
 * @param {Object} to The target route.
 * @param {{ assignmentId: *, attemptId: * }} evaluation
 * @returns {boolean}
 */
const mayNavigateDuringEvaluation = (to, evaluation) =>
    to.name === 'automated-survey-attempt'
        ? isSameEvaluation(to.params, evaluation)
        : LOCKED_EVALUATION_ROUTE_NAMES.includes(to.name);

/**
 * The evaluation the learner is locked into, decided from the route they are
 * leaving: the evaluation page itself, or identity verification entered from it.
 *
 * @param {Object} from The route being left.
 * @returns {Object|null} The evaluation route to send the learner back to.
 */
const lockedEvaluationRouteFrom = from => {
    if (from?.name === 'automated-survey-attempt') {
        return { name: 'automated-survey-attempt', params: { ...from.params } };
    }

    if (from?.name === 'identity-verification-attempt' && lastLockedEvaluationRoute) {
        const activeId = store.getters['User/get']('quiz_active_id');
        const { attemptId } = lastLockedEvaluationRoute.params;

        // A remembered route from an earlier evaluation never applies to another attempt.
        if (activeId && attemptId && String(activeId) !== String(attemptId)) {
            return null;
        }

        return lastLockedEvaluationRoute;
    }

    return null;
};

/**
 * @param {Object} activeAttempt An active attempt with its automated survey assignment.
 * @returns {Object} The evaluation attempt route.
 */
const automatedSurveyAttemptRoute = activeAttempt => ({
    name: 'automated-survey-attempt',
    params: {
        id: activeAttempt.automated_survey_assignment.survey_id,
        assignmentId: activeAttempt.automated_survey_assignment.id,
        attemptId: activeAttempt.id,
    },
    replace: true,
});

/**
 * Handles navigation guards for quiz and survey attempts.
 *
 * @param {Object} to The target Route Object being navigated to.
 * @param {Object} from The current Route Object being navigated from.
 * @param {Function} next This function must be called to resolve the hook.
 */
export default async (to, from, next) => {
    // Directly proceed if navigating to '403' page
    if (to.name === '403') return next();

    // Corporate clients take quizzes INLINE on the activity page (QuizCorporate +
    // useQuizActivity) — there is no separate quiz-attempt route / lockdown takeover.
    // Skip this guard entirely so a refresh mid/post-attempt never redirects them
    // into the stanbridge quiz-attempt pipeline.
    if (isCorporateClient()) return next();

    try {
        const { user } = store.state.Auth;

        // Check if user is not logged in or does not have permission to view any quiz attempts
        if (!user || gate.denies('viewAny', 'QuizAttempt')) {
            return next();
        }

        // Determine if user has an active quiz and if the current attempt is already hydrated
        const userHasQuizActiveId = store.getters['User/hasActiveQuiz'];
        const userHasQuizData = !!store.getters['User/get']('quiz_id') && !!store.getters['User/get']('quiz_activity');
        const hasHydratedAttempt = store.getters['QuizAttempt/has'](to.params?.id || 0);

        // In-app navigation away from a locked evaluation: send the learner straight
        // back, deciding from the store before any await. CheckAuth does not await
        // next() for a signed-in user, so the router confirms the navigation before
        // the active-attempt lookup below returns and discards a redirect issued
        // after it (ClickUp 868ma51n2). `quiz_active_id` is the flag the server locks
        // on, so this matches the 403s the target page would otherwise hit. This also
        // covers leaving identity verification that interrupted the evaluation, and
        // opening another evaluation's page.
        if (!userHasQuizActiveId) {
            lastLockedEvaluationRoute = null;
        }

        const lockedEvaluationRoute = userHasQuizActiveId ? lockedEvaluationRouteFrom(from) : null;

        if (lockedEvaluationRoute) {
            lastLockedEvaluationRoute = lockedEvaluationRoute;

            if (!mayNavigateDuringEvaluation(to, lockedEvaluationRoute.params)) {
                return next({ ...lockedEvaluationRoute, replace: true });
            }
        }

        // Skip further checks while moving within the hydrated attempt (e.g. between
        // questions). Only on attempt routes: another page's `:id` can collide with
        // the hydrated quiz id and would otherwise skip the lockdown redirect.
        if (userHasQuizActiveId && hasHydratedAttempt && isAttemptLockdownRouteName(to.name)) {
            return next();
        }

        // Build query to fetch active quiz attempts
        const inProgressQuery = new QueryBuilder('quiz-attempts')
            .filter('active_attempt', 1)
            .include('automatedSurveyAssignment.survey');

        // Fetch the first active attempt, if any
        const activeAttempt = first(
            await axios.get(inProgressQuery.toUrl(), { errorHandle: false }).then(resp => resp.data.data)
        );

        // Determine if there's a lockdown condition in the active attempt
        const lockdown = activeAttempt
            ? activeAttempt.quiz_activity?.lockdown ||
              activeAttempt.survey_activity?.lockdown ||
              activeAttempt.automated_survey_assignment?.is_locked_now
            : null;

        // Evaluation attempts: a locked evaluation keeps the learner in it (ClickUp
        // 868ma51n2); an unlocked one lets them navigate freely. This redirect only
        // takes effect when the pipeline awaits this guard (the first navigation of a
        // page load); in-app navigation away is handled by the synchronous check above.
        if (activeAttempt?.automated_survey_assignment) {
            const evaluationLocked = lockdown || userHasQuizActiveId;
            const evaluation = {
                assignmentId: activeAttempt.automated_survey_assignment.id,
                attemptId: activeAttempt.id,
            };

            if (evaluationLocked && !mayNavigateDuringEvaluation(to, evaluation)) {
                return next(automatedSurveyAttemptRoute(activeAttempt));
            }

            return next();
        }

        // Handle quiz or survey lockdown situations
        if (activeAttempt) {
            // Determine the type of the active attempt
            const typeKey =
                activeAttempt.survey_activity_id || activeAttempt.automated_survey_assignment ? 'survey' : 'quiz';

            // Select the appropriate activity based on the type
            const activity = activeAttempt.quiz_activity_id
                ? activeAttempt.quiz_activity
                : activeAttempt.survey_activity;

            // Extract the quiz version ID
            const quizId = activeAttempt[`${typeKey}_activity`].quiz_version_id;

            // If the attempt is not hydrated, no quiz data present, and in lockdown
            if (!hasHydratedAttempt && !userHasQuizData && lockdown) {
                const { quiz } = activeAttempt;

                // Attach the quiz activity to the quiz object
                quiz.quiz_activity = activeAttempt[`${typeKey}_activity`];

                // Build a query to fetch remediation data
                const remediationQuery = new QueryBuilder('remediation')
                    .filter('course_id', activity.activity.course_id)
                    .filter('activity_id', activity.activity.id)
                    .filter('user_id', user.id);

                // Fetch and process the remediation data
                await Remediation.api().get(remediationQuery.toUrl());
                const remediation = Remediation.query()
                    .where('course_id', activity.activity.course_id)
                    .where('activity_id', activity.activity.id)
                    .where('user_id', user.id)
                    .first();

                // Apply time limit from remediation if applicable
                if (remediation && Number(get(remediation, 'quiz_time_limit')) > 0) {
                    quiz.quiz_activity.time_limit = remediation.quiz_time_limit;
                }

                // Update the store with the new quiz attempt data
                await store.dispatch('QuizAttempt/add', { ...quiz });
                await store.commit('User/SET', { field: 'quiz_id', value: quizId });
                await store.commit('User/SET', { field: 'quiz_activity', value: activity });
                await store.commit('User/SET', { field: 'quiz_active_id', value: activeAttempt.id });

                // Hydrate the quiz attempt in the store
                await store.dispatch(`QuizAttempt/${quiz.id}/hydrateQuizAttempt`, { quiz_attempt: activeAttempt });
            }

            // Redirect if not on an attempt page and in lockdown. Match the attempt
            // route names explicitly: `includes('attempt')` also let Manage Quiz
            // Attempts and Attempt Authorizations through.
            if (!isAttemptLockdownRouteName(to.name) && lockdown) {
                // Redirect to the appropriate attempt page based on the type
                return next({
                    name: `${typeKey}-attempt`,
                    params: {
                        activityId: activity.activity.id,
                        courseId: activity.activity.course_id,
                        id: quizId,
                        quizActivityId: activeAttempt[`${typeKey}_activity_id`],
                        quizVersionId: quizId,
                    },
                    replace: true,
                });
            }

            // In a scenario where the user is hard refreshing on the quiz or survey attempt page
            // Vue Router will lose the route name and important parameters, below we attempt
            // to add the missing parameters back to the route object
            if (!to.name && from.path === '/' && (to.path.includes('quiz') || to.path.includes('survey'))) {
                const updatedRoute = {
                    ...to,
                    name: `${typeKey}-attempt`,
                    params: {
                        ...to.params,
                        attemptId: activeAttempt.id,
                        quizVersionId: quizId,
                        quizActivityId: activeAttempt[`${typeKey}_activity_id`],
                    },
                };

                return next(updatedRoute);
            }
        }

        // Handle navigation for cases where there's no active attempt but navigation is attempted to a quiz or survey
        const checklist = ['quiz-attempt', 'survey-attempt'];

        // Check if coming from a hard refresh, typically the name and redirect properties
        // of both the to and from objects will be undefined
        const isHardRefresh = !to.name && !to.redirect && !from.name && !from.redirect;

        // Redirect the student if there is no active attempt, and the target route is a quiz or survey.
        // In the event where the navigation event is form a hard refresh, redirect to the activity view page as well.
        // This is to prevent an edge case where the student is redirected to the attempt page after ending an attempt.
        if (isEmpty(activeAttempt) && (checklist.some(item => to.name === item) || isHardRefresh)) {
            return next({
                name: 'activity-view',
                params: { ...to.params },
            });
        }

        // Proceed with the original navigation if none of the above conditions are met
        return next();
    } catch (error) {
        handleResponseError(error);
    }

    // Fallback next call to ensure navigation resolution
    return next();
};
